import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FlaskConical,
  ArrowLeft,
  LogOut,
  Loader2,
  BarChart3,
  Activity,
  Clock,
  Award,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { COURSES } from '@/config/courses.config';
import {
  studentProgressService,
  type CourseOverview,
  type ExperimentDetail,
  type ActivityEntry,
} from '@/services/studentProgressService';
import { feedbackService, type AIFeedbackEntry } from '@/services/feedbackService';
import { CourseProgressRing, ActivityTimeline, QuizScoreChart } from '@/components/progress';
import { QuizScoreBadge } from '@/components/quiz';
import AIFeedbackCard from '@/components/feedback/AIFeedbackCard';

type TabId = 'experiments' | 'timeline' | 'quizzes';

export default function StudentProgressPage() {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const { state, logout } = useApp();

  const course = courseId ? COURSES[courseId] : null;

  // Data state
  const [overview, setOverview] = useState<CourseOverview | null>(null);
  const [experiments, setExperiments] = useState<ExperimentDetail[]>([]);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [quizScores, setQuizScores] = useState<{ experimentName: string; percentage: number }[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<TabId>('experiments');
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [expandedExp, setExpandedExp] = useState<number | null>(null);
  const [feedbackMap, setFeedbackMap] = useState<Record<number, AIFeedbackEntry | null>>({});
  const [feedbackLoading, setFeedbackLoading] = useState<number | null>(null);

  // Fetch all data on mount
  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [overviewData, experimentsData, timelineData, quizData] = await Promise.allSettled([
          studentProgressService.getCourseOverview(courseId!),
          studentProgressService.getExperimentDetails(courseId!),
          studentProgressService.getTimeline(courseId!, 1),
          studentProgressService.getQuizSummary(courseId!),
        ]);

        if (cancelled) return;

        if (overviewData.status === 'fulfilled') {
          setOverview(overviewData.value);
        }
        if (experimentsData.status === 'fulfilled') {
          setExperiments(experimentsData.value);
        }
        if (timelineData.status === 'fulfilled') {
          setActivities(timelineData.value.activities);
          setTimelineTotal(timelineData.value.total);
        }
        if (quizData.status === 'fulfilled' && Array.isArray(quizData.value)) {
          setQuizScores(
            quizData.value.map((q: any) => ({
              experimentName: q.experimentName || `Exp ${q.experimentNumber}`,
              percentage: q.percentage ?? q.bestScore ?? 0,
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load progress data. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  // Load more timeline entries
  const loadMoreTimeline = async () => {
    if (!courseId || timelineLoading) return;
    setTimelineLoading(true);
    try {
      const nextPage = timelinePage + 1;
      const data = await studentProgressService.getTimeline(courseId, nextPage);
      setActivities((prev) => [...prev, ...data.activities]);
      setTimelinePage(nextPage);
      setTimelineTotal(data.total);
    } catch {
      // silently fail
    } finally {
      setTimelineLoading(false);
    }
  };

  // Fetch existing feedback when expanding an experiment
  const handleExpandExperiment = async (expId: number, expNumber: number) => {
    const newExpanded = expandedExp === expId ? null : expId;
    setExpandedExp(newExpanded);
    if (newExpanded && courseId && feedbackMap[expNumber] === undefined) {
      try {
        const fb = await feedbackService.getExperimentFeedback(courseId, expNumber);
        setFeedbackMap(prev => ({ ...prev, [expNumber]: fb }));
      } catch {
        setFeedbackMap(prev => ({ ...prev, [expNumber]: null }));
      }
    }
  };

  const handleGenerateFeedback = async (expNumber: number) => {
    if (!courseId || feedbackLoading !== null) return;
    setFeedbackLoading(expNumber);
    try {
      const fb = await feedbackService.generateFeedback(courseId, expNumber);
      setFeedbackMap(prev => ({ ...prev, [expNumber]: fb }));
    } catch {
      // Silently fail - card will show empty state with generate button
    } finally {
      setFeedbackLoading(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBack = () => {
    navigate(`/course/${courseId}`);
  };

  // Status helpers
  function getStatusLabel(status: string): string {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  }

  function getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'in_progress':
        return 'status-in-progress';
      default:
        return 'status-not-started';
    }
  }

  function formatLastActivity(dateStr: string | null): string {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (!course) {
    return (
      <div className="progress-page">
        <div className="progress-error-state">
          <h2>Course not found</h2>
          <button onClick={() => navigate('/courses')} className="sp-btn sp-btn-primary">
            Back to Courses
          </button>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  return (
    <div className="progress-page">
      {/* Header */}
      <header className="sp-header">
        <div className="sp-header-left">
          <button className="sp-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="sp-brand">
            <FlaskConical size={24} />
            <span>BDP vLab</span>
          </div>
          <span className="sp-course-badge">{course.code}</span>
        </div>
        <div className="sp-header-center">
          <h2 className="sp-page-title">My Progress</h2>
        </div>
        <div className="sp-header-right">
          <span className="sp-user-name">{state.studentInfo?.name || 'Student'}</span>
          <button className="sp-logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="sp-content">
        {loading ? (
          <div className="sp-loading">
            <Loader2 size={40} className="sp-spin" />
            <p>Loading your progress...</p>
          </div>
        ) : error ? (
          <div className="sp-error-msg">
            <p>{error}</p>
            <button className="sp-btn sp-btn-primary" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Overview cards */}
            <section className="sp-overview-cards">
              <div className="sp-overview-card sp-card-highlight">
                <div className="sp-card-ring">
                  <CourseProgressRing
                    percentage={overview?.completionPercentage ?? 0}
                    size={88}
                  />
                </div>
                <div className="sp-card-info">
                  <span className="sp-card-value">
                    {overview?.experimentsCompleted ?? 0}/{overview?.totalExperiments ?? 11}
                  </span>
                  <span className="sp-card-label">Experiments Completed</span>
                </div>
              </div>

              <div className="sp-overview-card">
                <div className="sp-card-icon" style={{ background: 'rgba(255, 90, 54, 0.15)', color: 'var(--primary, #FF5A36)' }}>
                  <BarChart3 size={24} />
                </div>
                <div className="sp-card-info">
                  <span className="sp-card-value">
                    {Math.round(overview?.completionPercentage ?? 0)}%
                  </span>
                  <span className="sp-card-label">Completion Rate</span>
                </div>
              </div>

              <div className="sp-overview-card">
                <div className="sp-card-icon" style={{ background: 'rgba(255, 234, 0, 0.15)', color: 'var(--accent, #FFEA00)' }}>
                  <Award size={24} />
                </div>
                <div className="sp-card-info">
                  <span className="sp-card-value">
                    {overview?.averageQuizScore != null
                      ? `${Math.round(overview.averageQuizScore)}%`
                      : '--'}
                  </span>
                  <span className="sp-card-label">Avg Quiz Score</span>
                </div>
              </div>

              <div className="sp-overview-card">
                <div className="sp-card-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6' }}>
                  <Activity size={24} />
                </div>
                <div className="sp-card-info">
                  <span className="sp-card-value">{overview?.totalActivities ?? 0}</span>
                  <span className="sp-card-label">Activities</span>
                </div>
              </div>
            </section>

            {/* Tab navigation */}
            <div className="sp-tabs">
              <button
                className={`sp-tab ${activeTab === 'experiments' ? 'active' : ''}`}
                onClick={() => setActiveTab('experiments')}
              >
                <FlaskConical size={16} />
                Experiments
              </button>
              <button
                className={`sp-tab ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
              >
                <Clock size={16} />
                Activity Timeline
              </button>
              <button
                className={`sp-tab ${activeTab === 'quizzes' ? 'active' : ''}`}
                onClick={() => setActiveTab('quizzes')}
              >
                <Award size={16} />
                Quiz Scores
              </button>
            </div>

            {/* Tab panels */}
            <div className="sp-tab-content">
              {/* Experiments Table */}
              {activeTab === 'experiments' && (
                <div className="sp-experiments-panel">
                  {experiments.length === 0 ? (
                    <div className="sp-empty">
                      <FlaskConical size={32} />
                      <p>No experiment data available yet.</p>
                    </div>
                  ) : (
                    <div className="sp-exp-table">
                      {/* Table header */}
                      <div className="sp-exp-row sp-exp-header">
                        <span className="sp-exp-col sp-col-num">#</span>
                        <span className="sp-exp-col sp-col-name">Experiment</span>
                        <span className="sp-exp-col sp-col-status">Status</span>
                        <span className="sp-exp-col sp-col-steps">Steps</span>
                        <span className="sp-exp-col sp-col-quiz">Quiz</span>
                        <span className="sp-exp-col sp-col-date">Last Activity</span>
                        <span className="sp-exp-col sp-col-expand" />
                      </div>

                      {/* Table rows */}
                      {experiments.map((exp) => (
                        <div key={exp.experimentId}>
                          <div
                            className={`sp-exp-row ${expandedExp === exp.experimentId ? 'expanded' : ''}`}
                            onClick={() => handleExpandExperiment(exp.experimentId, exp.experimentNumber)}
                          >
                            <span className="sp-exp-col sp-col-num">{exp.experimentNumber}</span>
                            <span className="sp-exp-col sp-col-name">{exp.experimentName}</span>
                            <span className="sp-exp-col sp-col-status">
                              <span className={`sp-status-badge ${getStatusClass(exp.status)}`}>
                                {getStatusLabel(exp.status)}
                              </span>
                            </span>
                            <span className="sp-exp-col sp-col-steps">
                              <span className="sp-step-progress">
                                <span
                                  className="sp-step-bar"
                                  style={{
                                    width:
                                      exp.totalSteps > 0
                                        ? `${(exp.stepsCompleted / exp.totalSteps) * 100}%`
                                        : '0%',
                                  }}
                                />
                              </span>
                              <span className="sp-step-count">
                                {exp.stepsCompleted}/{exp.totalSteps}
                              </span>
                            </span>
                            <span className="sp-exp-col sp-col-quiz">
                              <QuizScoreBadge percentage={exp.bestQuizScore} size="sm" />
                            </span>
                            <span className="sp-exp-col sp-col-date">
                              {formatLastActivity(exp.lastActivity)}
                            </span>
                            <span className="sp-exp-col sp-col-expand">
                              {expandedExp === exp.experimentId ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </span>
                          </div>

                          {/* Expanded detail */}
                          {expandedExp === exp.experimentId && (
                            <div className="sp-exp-detail">
                              <div className="sp-detail-grid">
                                <div className="sp-detail-item">
                                  <span className="sp-detail-label">Parts Completed</span>
                                  <span className="sp-detail-value">
                                    {exp.partsCompleted.length > 0
                                      ? exp.partsCompleted.join(', ')
                                      : 'None'}
                                  </span>
                                </div>
                                <div className="sp-detail-item">
                                  <span className="sp-detail-label">Latest Quiz Score</span>
                                  <span className="sp-detail-value">
                                    {exp.quizScore != null ? `${Math.round(exp.quizScore)}%` : '--'}
                                  </span>
                                </div>
                                <div className="sp-detail-item">
                                  <span className="sp-detail-label">Best Quiz Score</span>
                                  <span className="sp-detail-value">
                                    {exp.bestQuizScore != null
                                      ? `${Math.round(exp.bestQuizScore)}%`
                                      : '--'}
                                  </span>
                                </div>
                                <div className="sp-detail-item">
                                  <span className="sp-detail-label">AI Feedback</span>
                                  <span className="sp-detail-value">
                                    {feedbackMap[exp.experimentNumber] ? (
                                      <span className="sp-feedback-yes">
                                        <MessageSquare size={12} /> Available
                                      </span>
                                    ) : (
                                      'Not yet'
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div className="sp-detail-actions">
                                <button
                                  className="sp-btn sp-btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/course/${courseId}/experiment/exp${exp.experimentNumber}`
                                    );
                                  }}
                                >
                                  {exp.status === 'not_started' ? 'Start' : 'Continue'} Experiment
                                </button>
                              </div>
                              <div className="sp-detail-feedback" onClick={(e) => e.stopPropagation()}>
                                <AIFeedbackCard
                                  feedback={feedbackMap[exp.experimentNumber] ?? null}
                                  loading={feedbackLoading === exp.experimentNumber}
                                  onGenerate={() => handleGenerateFeedback(exp.experimentNumber)}
                                  experimentName={exp.experimentName}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Activity Timeline */}
              {activeTab === 'timeline' && (
                <div className="sp-timeline-panel">
                  <ActivityTimeline activities={activities} />
                  {activities.length < timelineTotal && (
                    <div className="sp-load-more">
                      <button
                        className="sp-btn sp-btn-outline"
                        onClick={loadMoreTimeline}
                        disabled={timelineLoading}
                      >
                        {timelineLoading ? (
                          <>
                            <Loader2 size={14} className="sp-spin" /> Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Quiz Scores Chart */}
              {activeTab === 'quizzes' && (
                <div className="sp-quizzes-panel">
                  <QuizScoreChart scores={quizScores} />
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .progress-page {
    min-height: 100vh;
    background: var(--bg-dark, #1A1A1A);
    color: var(--text-primary, #FFFFFF);
    display: flex;
    flex-direction: column;
  }

  /* Header */
  .sp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background: var(--bg-surface, #2C2C2C);
    border-bottom: 1px solid var(--border, #4A4A4A);
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .sp-header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .sp-header-center {
    display: flex;
    align-items: center;
  }
  .sp-header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .sp-back-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary, #CCCCCC);
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    border-radius: 6px;
    transition: all 0.2s;
  }
  .sp-back-btn:hover {
    color: var(--primary, #FF5A36);
    background: rgba(255, 90, 54, 0.1);
  }
  .sp-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
    color: var(--primary, #FF5A36);
  }
  .sp-course-badge {
    background: var(--primary, #FF5A36);
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
  .sp-page-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: var(--text-primary, #FFFFFF);
  }
  .sp-user-name {
    color: var(--text-secondary, #CCCCCC);
    font-size: 14px;
  }
  .sp-logout-btn {
    background: transparent;
    border: 1px solid var(--border, #4A4A4A);
    color: var(--text-secondary, #CCCCCC);
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    transition: all 0.2s;
  }
  .sp-logout-btn:hover {
    border-color: var(--danger, #EF4444);
    color: var(--danger, #EF4444);
  }

  /* Content */
  .sp-content {
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
    padding: 28px 24px 48px;
    flex: 1;
  }

  /* Loading / Error */
  .sp-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 80px 0;
    color: var(--text-muted, #999999);
  }
  .sp-loading p {
    margin: 0;
    font-size: 14px;
  }
  .sp-spin {
    animation: sp-spin 1s linear infinite;
  }
  @keyframes sp-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .sp-error-msg {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px 0;
    color: var(--danger, #EF4444);
    text-align: center;
  }
  .sp-error-msg p {
    margin: 0;
    font-size: 14px;
  }
  .progress-error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    gap: 16px;
  }

  /* Overview Cards */
  .sp-overview-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 28px;
  }
  .sp-overview-card {
    background: var(--bg-surface, #2C2C2C);
    border: 1px solid var(--border, #4A4A4A);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: border-color 0.2s;
  }
  .sp-overview-card:hover {
    border-color: var(--primary, #FF5A36);
  }
  .sp-card-highlight {
    grid-column: span 1;
  }
  .sp-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sp-card-ring {
    flex-shrink: 0;
  }
  .sp-card-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .sp-card-value {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary, #FFFFFF);
    line-height: 1.1;
  }
  .sp-card-label {
    font-size: 12px;
    color: var(--text-muted, #999999);
  }

  /* Tabs */
  .sp-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 20px;
    background: var(--bg-surface, #2C2C2C);
    border-radius: 10px;
    padding: 4px;
    border: 1px solid var(--border, #4A4A4A);
  }
  .sp-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: var(--text-muted, #999999);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  .sp-tab:hover {
    color: var(--text-secondary, #CCCCCC);
    background: var(--bg-elevated, #333);
  }
  .sp-tab.active {
    background: var(--primary, #FF5A36);
    color: white;
    font-weight: 600;
  }

  /* Tab Content */
  .sp-tab-content {
    background: var(--bg-surface, #2C2C2C);
    border: 1px solid var(--border, #4A4A4A);
    border-radius: 12px;
    padding: 20px;
    min-height: 300px;
  }

  /* Experiments Table */
  .sp-exp-table {
    display: flex;
    flex-direction: column;
  }
  .sp-exp-row {
    display: grid;
    grid-template-columns: 40px 1fr 110px 130px 70px 120px 30px;
    gap: 12px;
    align-items: center;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .sp-exp-row:not(.sp-exp-header):hover {
    background: var(--bg-elevated, #333);
  }
  .sp-exp-row.expanded {
    background: var(--bg-elevated, #333);
    border-radius: 8px 8px 0 0;
  }
  .sp-exp-header {
    cursor: default;
    border-bottom: 1px solid var(--border, #4A4A4A);
    margin-bottom: 4px;
  }
  .sp-exp-header .sp-exp-col {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted, #999999);
  }
  .sp-exp-col {
    font-size: 13px;
    color: var(--text-secondary, #CCCCCC);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sp-col-num {
    font-weight: 700;
    color: var(--primary, #FF5A36);
    text-align: center;
  }
  .sp-col-name {
    font-weight: 500;
    color: var(--text-primary, #FFFFFF);
  }
  .sp-col-expand {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted, #999999);
  }

  /* Status badges */
  .sp-status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .status-completed {
    background: rgba(0, 168, 107, 0.15);
    color: var(--secondary, #00A86B);
    border: 1px solid rgba(0, 168, 107, 0.3);
  }
  .status-in-progress {
    background: rgba(59, 130, 246, 0.15);
    color: #3B82F6;
    border: 1px solid rgba(59, 130, 246, 0.3);
  }
  .status-not-started {
    background: rgba(153, 153, 153, 0.1);
    color: var(--text-muted, #999999);
    border: 1px solid rgba(153, 153, 153, 0.2);
  }

  /* Step progress bar (mini) */
  .sp-col-steps {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sp-step-progress {
    flex: 1;
    height: 6px;
    background: var(--bg-dark, #1A1A1A);
    border-radius: 3px;
    overflow: hidden;
  }
  .sp-step-bar {
    display: block;
    height: 100%;
    background: var(--secondary, #00A86B);
    border-radius: 3px;
    transition: width 0.4s ease;
  }
  .sp-step-count {
    font-size: 11px;
    color: var(--text-muted, #999999);
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Expanded detail panel */
  .sp-exp-detail {
    background: var(--bg-elevated, #333);
    border-radius: 0 0 8px 8px;
    padding: 16px 20px;
    margin-bottom: 4px;
    animation: sp-slideDown 0.2s ease;
  }
  @keyframes sp-slideDown {
    from { opacity: 0; max-height: 0; }
    to { opacity: 1; max-height: 200px; }
  }
  .sp-detail-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 12px;
  }
  .sp-detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .sp-detail-label {
    font-size: 11px;
    color: var(--text-muted, #999999);
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .sp-detail-value {
    font-size: 13px;
    color: var(--text-primary, #FFFFFF);
    font-weight: 500;
  }
  .sp-feedback-yes {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #3B82F6;
  }
  .sp-detail-actions {
    margin-bottom: 12px;
  }
  .sp-detail-feedback {
    margin-top: 4px;
  }

  /* Buttons */
  .sp-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
  }
  .sp-btn-primary {
    background: var(--primary, #FF5A36);
    color: white;
  }
  .sp-btn-primary:hover {
    background: var(--primary-dark, #E04420);
    transform: translateY(-1px);
  }
  .sp-btn-outline {
    background: transparent;
    border: 1px solid var(--border, #4A4A4A);
    color: var(--text-secondary, #CCCCCC);
  }
  .sp-btn-outline:hover {
    border-color: var(--primary, #FF5A36);
    color: var(--primary, #FF5A36);
  }
  .sp-btn-outline:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .sp-btn-sm {
    padding: 6px 14px;
    font-size: 12px;
    background: var(--primary, #FF5A36);
    color: white;
  }
  .sp-btn-sm:hover {
    background: var(--primary-dark, #E04420);
  }

  /* Empty state */
  .sp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 48px 16px;
    color: var(--text-muted, #999999);
    text-align: center;
  }
  .sp-empty p {
    margin: 0;
    font-size: 13px;
    max-width: 280px;
    line-height: 1.5;
  }

  /* Timeline panel */
  .sp-timeline-panel {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 8px;
  }
  .sp-load-more {
    display: flex;
    justify-content: center;
    padding: 16px 0 4px;
  }

  /* Quizzes panel */
  .sp-quizzes-panel {
    padding: 8px 0;
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .sp-overview-cards {
      grid-template-columns: repeat(2, 1fr);
    }
    .sp-exp-row {
      grid-template-columns: 32px 1fr 90px 110px 60px 30px;
    }
    .sp-col-date {
      display: none;
    }
    .sp-detail-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    .sp-header {
      padding: 10px 16px;
      flex-wrap: wrap;
      gap: 8px;
    }
    .sp-header-center {
      order: 3;
      width: 100%;
      justify-content: center;
    }
    .sp-content {
      padding: 16px 12px 32px;
    }
    .sp-overview-cards {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    .sp-overview-card {
      padding: 14px;
      gap: 10px;
    }
    .sp-card-value {
      font-size: 20px;
    }
    .sp-tabs {
      overflow-x: auto;
    }
    .sp-tab {
      padding: 8px 12px;
      font-size: 12px;
      white-space: nowrap;
    }
    .sp-exp-row {
      grid-template-columns: 28px 1fr 80px 30px;
      gap: 8px;
      padding: 10px 10px;
    }
    .sp-col-steps,
    .sp-col-quiz,
    .sp-col-date {
      display: none;
    }
    .sp-exp-header .sp-col-steps,
    .sp-exp-header .sp-col-quiz,
    .sp-exp-header .sp-col-date {
      display: none;
    }
    .sp-detail-grid {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    .sp-brand span {
      display: none;
    }
  }
`;
